// features/workstations/dashboard-sections/window-risk-map.js
// ═══════════════════════════════════════════════════════════
// Window Risk Map (Day Trading)
// Visual banding across the trading window showing historical
// risk-by-time-of-day.
// ═══════════════════════════════════════════════════════════
// V1: renders the window in 15-minute bands using the
// opening/closing times from config.identity. Each band has
// a static risk profile (opening = high, mid = prime,
// closing = falling edge).
// V2: the profile is driven by this user's own trade log.
// ═══════════════════════════════════════════════════════════

import ShiftRead from './shift-read';

const DEFAULT_BANDS = [
  { label: '09:30', tone: 'high',  note: 'First 15 — chaos, avoid' },
  { label: '09:45', tone: 'mid',   note: 'Structure forming' },
  { label: '10:00', tone: 'prime', note: 'Prime entry window' },
  { label: '10:30', tone: 'prime', note: 'Prime entry window' },
  { label: '11:00', tone: 'mid',   note: 'Momentum fading' },
  { label: '11:30', tone: 'low',   note: 'Edge gone — close or hold' },
];

export default function WindowRiskMapSection({ workstation }) {
  const identity = workstation?.config?.identity || {};
  const start = identity.trading_window_start || '09:30';
  const end = identity.trading_window_end || '11:30';

  // If the config window matches the default, use the rich band
  // definitions. For different windows, render a generic 4-band
  // split (opening / early / late / closing).
  const useDefault = start === '09:30' && end === '11:30';
  const bands = useDefault
    ? DEFAULT_BANDS
    : [
        { label: start, tone: 'high', note: 'Opening volatility' },
        { label: '',    tone: 'prime', note: 'Prime window' },
        { label: '',    tone: 'mid',  note: 'Fading edge' },
        { label: end,   tone: 'low',  note: 'Window closing' },
      ];

  const now = nowBandIndex(bands.length);

  return (
    <section className="ws-section ws-section-window">
      <div className="ws-section-header">
        <div className="ws-section-label">Window Risk Map</div>
        <div className="ws-section-time">
          {start} – {end} ET
        </div>
      </div>
      <div className="ws-section-body">
        <div className="ws-window-strip">
          {bands.map((b, i) => (
            <div
              key={i}
              className={`ws-window-band ws-window-band-${b.tone}${now === i ? ' is-now' : ''}`}
              title={b.note}
            >
              <div className="ws-window-band-label">{b.label}</div>
              <div className="ws-window-band-note">{b.note}</div>
              {now === i && <div className="ws-window-now-tag">Now</div>}
            </div>
          ))}
        </div>

        <div className="ws-window-legend">
          <span className="ws-window-legend-item"><span className="ws-window-legend-dot is-high" /> High risk</span>
          <span className="ws-window-legend-item"><span className="ws-window-legend-dot is-prime" /> Prime</span>
          <span className="ws-window-legend-item"><span className="ws-window-legend-dot is-mid" /> Mid</span>
          <span className="ws-window-legend-item"><span className="ws-window-legend-dot is-low" /> Edge gone</span>
        </div>

        <ShiftRead>
          Trade in the prime bands. The opening 15 and the closing 30
          are where most of your red days come from.
        </ShiftRead>
      </div>
    </section>
  );
}

// Best-effort "which band are we in right now" using the user's
// local time converted to ET. Good enough for a visual cue — not
// used for any gating logic.
function nowBandIndex(bandCount) {
  try {
    const nowET = new Date().toLocaleString('en-US', { timeZone: 'America/New_York' });
    const d = new Date(nowET);
    const minutes = d.getHours() * 60 + d.getMinutes();
    const windowStart = 9 * 60 + 30;   // 09:30
    const windowEnd = 11 * 60 + 30;    // 11:30
    if (minutes < windowStart || minutes > windowEnd) return -1;
    const frac = (minutes - windowStart) / (windowEnd - windowStart);
    return Math.min(bandCount - 1, Math.floor(frac * bandCount));
  } catch {
    return -1;
  }
}
